import { useState } from "react";
import { Tag, ArrowUpRight, Check, Copy } from "@phosphor-icons/react";

const discounts = [
  {
    id: "bucked-up",
    brand: "Bucked Up",
    category: "Supplements",
    code: "SLAYJ2025",
    discount: "10% off",
    blurb: "What I take pre and post training. Clean ingredients, no nonsense.",
    url: "https://bckd.co/n3iXj9V",
  },
  {
    id: "deemed-fit",
    brand: "Deemed Fit",
    category: "Apparel",
    code: "SLAYJ",
    discount: "15% off",
    blurb: "Training fits that actually move with you — built for real workouts.",
    url: "https://deemedfit.co/?ref=amxjvvjf",
  },
  {
    id: "jrip",
    brand: "Jrip LA",
    category: "Streetwear",
    code: "SLAYJ",
    discount: "15% off",
    blurb: "LA-built athleisure with attitude. Lift, lounge, repeat.",
    url: "https://jripla.com/?ref=ouxeiqle",
  },
  {
    id: "lyftlyfe",
    brand: "Lyftlyfe Apparel",
    category: "Lifting gear",
    code: "SLAYJ",
    discount: "10% off",
    blurb: "Strength-first basics — the staples in my training rotation.",
    url: "https://lyftlyfeapparel.com/?ref=Slayj",
  },
  {
    id: "vtl",
    brand: "VTL Apparel",
    category: "Apparel",
    code: "SLAYJ",
    discount: "10% off",
    blurb: "Performance fits that hold up rep after rep — built for the long game.",
    url: "https://vtlapparel.com/discount/SlayJ",
  },
  {
    id: "psycho-pharma",
    brand: "Psycho Pharma",
    category: "Supplements",
    code: "SLAYJ",
    discount: "10% off",
    blurb: "High-stim formulas for the days you need the extra kick.",
    url: "https://www.psychopharma.com/SlayJ",
  },
];

function DiscountCard({ d }) {
  const [copied, setCopied] = useState(false);

  const copyCode = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = d.code;
    const showCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(showCopied).catch(() => fallbackCopy(text, showCopied));
      } else {
        fallbackCopy(text, showCopied);
      }
    } catch {
      fallbackCopy(text, showCopied);
    }
  };

  const fallbackCopy = (text, onDone) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      onDone();
    } catch {
      onDone();
    }
  };

  return (
    <a
      href={d.url}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={`discount-${d.id}`}
      className="group relative border border-white/10 bg-[#0F0F0F] hover:border-[#FF1B91] hover:bg-[#141414] transition-all p-6 flex flex-col"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="w-10 h-10 border border-white/15 flex items-center justify-center text-[#FF1B91] group-hover:border-[#FF1B91] transition-colors">
          <Tag size={18} weight="bold" />
        </div>
        <ArrowUpRight
          size={20}
          weight="bold"
          className="text-white/30 group-hover:text-[#FF1B91] group-hover:rotate-45 transition-all duration-300"
        />
      </div>

      <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 font-semibold mb-2">
        {d.category}
      </div>
      <h3 className="font-body font-semibold text-xl tracking-tight mb-3 leading-snug">
        {d.brand}
      </h3>
      <p className="text-sm text-white/65 leading-relaxed mb-6 flex-1">
        {d.blurb}
      </p>

      <div className="border-t border-white/10 pt-4 flex items-end justify-between gap-3">
        <button
          type="button"
          onClick={copyCode}
          data-testid={`copy-code-${d.id}`}
          className="flex-1 text-left group/code"
          aria-label={`Copy code ${d.code}`}
        >
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 font-semibold mb-1 flex items-center gap-1.5">
            {copied ? <>Copied <Check size={11} weight="bold" /></> : <>Tap to copy <Copy size={11} weight="bold" /></>}
          </div>
          <div className="font-mono font-bold text-[#FF1B91] tracking-widest text-sm group-hover/code:text-[#FF3FA3] transition-colors">
            {d.code}
          </div>
        </button>
        <div className="text-right">
          <div className="font-display font-black text-lg text-white leading-none whitespace-nowrap">
            {d.discount}
          </div>
        </div>
      </div>
    </a>
  );
}

export default function Discounts() {
  return (
    <section
      id="discounts"
      data-testid="discounts-section"
      className="relative px-6 md:px-12 lg:px-20 py-16 lg:py-20 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#FF1B91]" />
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Slay-Approved / 09</p>
            </div>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tighter">
              Things I actually{" "}
              <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">use &amp; love.</span>
            </h2>
          </div>
          <p className="md:max-w-sm text-white/70 leading-relaxed">
            Brands and tools I personally trust. Use my codes for a discount — small kickback supports the brand and keeps Sweat With Slay running.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {discounts.map((d) => (
            <DiscountCard key={d.id} d={d} />
          ))}
        </div>

        <p className="mt-10 text-center text-[11px] uppercase tracking-[0.3em] text-white/40">
          Disclosure: I may earn a small commission when you use these codes — at no extra cost to you.
        </p>
      </div>
    </section>
  );
}
